import {
  HardeningConfigSchema,
  HardeningReportSchema,
  ServerArtifactSchema,
  sha256Hex,
  canonicalSha256,
  type HardeningConfigInput,
  type HardeningReport,
  type PoisoningFinding,
  type PoisoningSeverity,
  type ServerArtifact,
  type ToolDesign
} from "@keystone/core";
import { scanForPoisoning } from "./scanner.js";
import { renderHardenedAuth } from "./verifier-impl.js";

const SEVERITY_ORDER: Record<PoisoningSeverity, number> = {
  info: 0,
  low: 1,
  medium: 2,
  high: 3,
  critical: 4
};

export interface HardenInput {
  design: ToolDesign;
  artifact: ServerArtifact;
  /** Caller-facing input — defaults are applied via Zod parse, no `as never` casts. */
  config: HardeningConfigInput;
}

export interface HardenOutput {
  artifact: ServerArtifact;
  report: HardeningReport;
}

/**
 * Apply hardening: replace `src/auth.ts` (default-deny stub) with a real `jose`-backed
 * verifier, scan all tools for poisoning, and emit a HardeningReport. The returned
 * artifact is a *new* ServerArtifact with the substituted file; the original is
 * left untouched.
 *
 * Naive baselines are scanned but not patched — the contrast is the point.
 */
export function harden(input: HardenInput): HardenOutput {
  // Apply Zod defaults so downstream code can rely on a fully-populated config.
  const config = HardeningConfigSchema.parse(input.config);

  const findings = scanForPoisoning({ design: input.design, artifact: input.artifact });

  const controlsApplied: string[] = ["oauth2.1-token-validation", "pii-redaction"];
  if (input.artifact.kind === "designed") {
    controlsApplied.push("least-privilege-scopes", "audit-log", "origin-guard", "tool-poisoning-scan", "helmet-headers");
  }

  const patched =
    input.artifact.kind === "designed"
      ? patchAuth(input.artifact, renderHardenedAuth({ ...config, serverName: input.design.serverName }))
      : input.artifact;

  const failThreshold = config.failThreshold;
  const passed = !findings.some((f) => SEVERITY_ORDER[f.severity] >= SEVERITY_ORDER[failThreshold]);

  const bySeverity = countSeverities(findings);

  const report = HardeningReportSchema.parse({
    serverDigest: patched.digest,
    serverKind: patched.kind,
    controlsApplied,
    findings,
    summary: { total: findings.length, bySeverity },
    passed,
    failThreshold,
    generatedAt: new Date(0).toISOString()
  });

  return { artifact: patched, report };
}

function patchAuth(artifact: ServerArtifact, newAuthSource: string): ServerArtifact {
  const files = artifact.files
    .filter((f) => f.path !== "src/auth.ts")
    .concat({ path: "src/auth.ts", content: newAuthSource, digest: sha256Hex(newAuthSource) })
    .sort((a, b) => a.path.localeCompare(b.path));
  const digest = canonicalSha256(files.map((f) => [f.path, f.digest]));
  return ServerArtifactSchema.parse({ ...artifact, files, digest });
}

function countSeverities(findings: PoisoningFinding[]): Record<string, number> {
  const out: Record<string, number> = { info: 0, low: 0, medium: 0, high: 0, critical: 0 };
  for (const f of findings) out[f.severity] = (out[f.severity] ?? 0) + 1;
  return out;
}
